<template>
  <div v-if="simulation != null" class="round-list">
    <drawer-block
      v-for="{ roundIndex, roundActionPlan, round } in roundBlocks"
      :key="roundIndex"
      :title="formatRoundIndex(roundIndex)"
      :selected="selectedRound != null && selectedRoundIndex === roundIndex"
      :class="{ 'is-faded-out': isRoundDisabled(roundIndex) }"
      :disabled="isRoundDisabled(roundIndex)"
      :name="roundIndex + ''"
      :open="
        roundIndex === selectedRoundIndex ||
        openRoundDrawers[roundIndex] == true
      "
      class="round-list__entry"
      @update:open="setRoundDrawerOpen(roundIndex, $event)"
    >
      <template #status>
        <span class="round-list__status">
          <span
            v-if="getIncidentIds(round).length > 0"
            :title="`${getIncidentIds(round).length} ${$t(
              'objectNames.incident',
              getIncidentIds(round).length
            )}`"
            class="round-list__status"
          >
            {{ getIncidentIds(round).length
            }}<store-item-icon type="incident" class="round-list__icon" />
          </span>
          <span
            v-if="
              roundActionPlan && roundActionPlan?.plannedActionIds?.length > 0
            "
            :title="`${roundActionPlan.plannedActionIds.length} ${$t(
              'objectNames.action',
              roundActionPlan.plannedActionIds.length
            )}`"
            class="round-list__status"
          >
            {{ roundActionPlan.plannedActionIds.length
            }}<store-item-icon type="action" class="round-list__icon" />
          </span>
          <span
            v-if="
              roundActionPlan && roundActionPlan?.scheduledEffects?.length > 0
            "
            :title="`${roundActionPlan.scheduledEffects.length} ${$t(
              'objectNames.scheduledEffect',
              roundActionPlan.scheduledEffects.length
            )}`"
            class="round-list__status"
          >
            {{ roundActionPlan.scheduledEffects.length
            }}<store-item-icon type="impulse" class="round-list__icon" />
          </span>
        </span>
      </template>
      <round-menu :simulation-id="simulation.id" :round-index="roundIndex" />
    </drawer-block>

    <teleport v-if="sortedOpenQuickInfos.length > 0" to="#app">
      <template
        v-for="info in sortedOpenQuickInfos"
        :key="`${info.type}-${info.itemType}-${info.itemId}-${
          info.roundIndex
        }-${info.itemRoundIndex || '--'}`"
      >
        <simulation-round-quick-info-popup
          v-if="info.type == 'round'"
          :info-item="(info as SimulationRoundQuickInfo)"
          :teleport-target="null"
          @close="closeSimulationQuickInfo($event)"
        />
        <simulation-quick-info-popup
          v-else
          :info-item="(info as SimulationItemQuickInfo)"
          :teleport-target="null"
          @close="closeSimulationQuickInfo($event)"
        />
      </template>
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { reduce } from "lodash-es";

import DrawerBlock from "@core/ui/DrawerBlock.vue";
import StoreItemIcon from "../StoreItemIcon.vue";

import RoundMenu from "@editor/items/simulationRound/RoundMenu.vue";

import SimulationQuickInfoPopup from "@editor/items/simulationRound/SimulationQuickInfoPopup.vue";
import SimulationRoundQuickInfoPopup from "./SimulationRoundQuickInfoPopup.vue";

import { useIncidentStore } from "../incident/incidents.store";
import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";
import { useSimulationStore } from "@editor/items/simulation/simulations.store";
import { formatRoundName } from "@editor/items/simulationRound/useRoundTime";

import type { SimulationItemQuickInfo, SimulationRoundQuickInfo } from "@editor/views/simulation/simulationView.store";
import type { SimulationRoundItem } from "./SimulationRoundItem";

const simulationViewStore = useSimulationViewStore();
const incidentStore = useIncidentStore();

const simulation = computed(() => simulationViewStore.simulation);
const rounds = computed(() => simulationViewStore.rounds);
const openQuickInfos = computed(() => simulationViewStore.openQuickInfos);
const selectedRound = computed(() => simulationViewStore.selectedRound);
const selectedRoundIndex = computed(() => simulationViewStore.selectedRoundIndex);
const currentRoundIndex = computed(() => simulationViewStore.currentRoundIndex);
const openRoundDrawers = computed(() => simulationViewStore.openRoundDrawers);

const sortedOpenQuickInfos = computed(() => {
  const all = reduce(
    openQuickInfos.value,
    (acc, infos) => {
      return acc.concat(
        infos as SimulationItemQuickInfo[] | SimulationRoundQuickInfo[]
      );
    },
    [] as (SimulationItemQuickInfo | SimulationRoundQuickInfo)[]
  );
  all.sort((a, b) => (a.lastUpdate ?? 0) - (b.lastUpdate ?? 0));
  return all;
});
const roundBlocks = computed(() => {
  if (!simulation.value) return [];
  const simulationId = simulation.value.id;
  return Array.from({ length: simulation.value.maxRounds }).map(
    (_, roundIndex) => ({
      roundIndex,
      round: rounds.value[roundIndex],
      roundActionPlan: useSimulationStore().getActionPlan(
        simulationId,
        roundIndex
      )
    })
  );
});

const closeSimulationQuickInfo = simulationViewStore.closeSimulationQuickInfo;
const setRoundDrawerOpen = simulationViewStore.setRoundDrawerOpen;

function getIncidentIds(round: SimulationRoundItem) {
  if (!round) return [];
  const incidentActivations = round.activatedIncidents;
  const activatedIncidents = incidentActivations.map((r) => incidentStore.items[r.incidentId] ?? { id: r.incidentId });
  return activatedIncidents.filter((incident) => incident.visibility != "hidden").map((r) => r.id);
}

function formatRoundIndex(roundIndex: number) {
  if (!simulation.value) return "";

  return formatRoundName(
    simulation.value.durationType,
    simulation.value.roundUnit,
    simulation.value.roundLength,
    roundIndex,
    simulation.value.timeStart
  );
}
function isRoundDisabled(roundIndex: number) {
  return simulation.value?.complete && roundIndex > currentRoundIndex.value;
}
</script>

<style lang="scss">
.drawer.is-faded-out {
  opacity: 0.5;
}
.round-list {
  height: 100%;
  overflow: auto;

  &__status {
    display: flex;
    align-items: center;
    & + & {
      margin-left: 0.4em;
    }
  }
  &__icon {
    color: var(--color-text-light);
    margin-left: 0.1em;
    font-size: 12px;
    width: 9px;
    text-align: center;
    margin-right: 0;
  }
}
</style>
